import {useState, useMemo, useEffect, useRef} from 'react'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {useQuery} from '@tanstack/react-query'
import {Plus} from 'lucide-react'
import {OutboundConnectorType, OutboundConnectorListResponseFields, OutboundConnectorCreateRequestFields} from '@/types/outbound-connector'
import {routes} from '@/lib/routes'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Button} from '@/components/ui/button'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {Label} from '@/components/ui/label'
import {Pagination} from '@/components/ui/pagination'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'

import {Combobox, ComboboxOption} from '@/components/ui/combobox'
import {SortableHeader} from '@/components/ui/sortable-header'
import {Skeleton} from '@/components/ui/skeleton'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {OutboundConnectorControllerConnectorsEndpoint} from '@/types/endpoint-contracts'
import {useAuth} from '@/contexts/auth-context'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {useOutboundConnectors, useCreateConnector} from '@/hooks/use-outbound-connectors'
import {permissions} from '@/lib/permissions'
import {formatTimestamp} from '@/lib/date-utils'
import {Icon} from '@/components/shared/Icon'
import {FormGrid} from '@/components/shared/FormGrid'
import {EntityLink} from '@/components/shared/EntityLink'
import {Stack} from '@/components/shared/Stack'
import {PageLayout} from '@/components/shared/PageLayout'
import {useDialog} from '@/hooks/useDialog'

export function Connectors() {
    const services = useServices()
    const navigate = useNavigate()
    const i18n = useI18n()
    usePageTitle(i18n.connectors.pageTitle)
    const {hasPermission} = useAuth()
    const createDialog = useDialog()
    const [formData, setFormData] = useState<{
        targetSystemId: string
        connectorType: string
    }>({
        targetSystemId: '',
        connectorType: 'SCIM_2_0'
    })

    const {connectorTypes} = useAppConfig()
    const canCreate = hasPermission(permissions.outboundConnectors.create)

    // WHY: A `?createForSystem={id}` query param means the user arrived from a
    // detail page's "Add Connector" affordance — open the create dialog with
    // the system pre-filled and strip the param so a reload doesn't re-trigger.
    // We also remember the origin so Cancel and successful Create return the
    // user to the originating system detail page instead of trapping them on
    // /connectors/outbound (issues 885, 932). The per-page origin capture is
    // a placeholder — the generic return-to capability lives in a separate
    // architecture issue (see Related on 932).
    const [searchParams, setSearchParams] = useSearchParams()
    const [originSystemId, setOriginSystemId] = useState<string | null>(null)
    // WHY: handle the ?createForSystem= prefill exactly once per mount. The
    // effect re-fires on every re-render (createDialog ref is unstable, and
    // setSearchParams is batched so the next render's searchParams may still
    // hold the param), so without this ref guard the effect re-enters and
    // setState in an infinite render loop — visible only under test/vitest
    // where the loop never settles (issue 932 placeholder).
    const handledPrefillRef = useRef(false)
    useEffect(() => {
        if (handledPrefillRef.current) return
        const prefillSystemId = searchParams.get('createForSystem')
        if (prefillSystemId && canCreate) {
            handledPrefillRef.current = true
            setFormData(prev => ({...prev, targetSystemId: prefillSystemId}))
            setOriginSystemId(prefillSystemId)
            createDialog.open()
            const next = new URLSearchParams(searchParams)
            next.delete('createForSystem')
            setSearchParams(next, {replace: true})
        }
    }, [searchParams, canCreate, createDialog, setSearchParams])

    // WHY: Server pagination state from URL — unified search + filter (issue 342.09).
    // The `q` term and typed filters travel through `searchAndFilters`; the URL is
    // the single source of truth, so `queryString` already carries them.
    const {
        page, pageSize, sortBy, sortDir, setSort,
        setPage, setPageSize, pageSizeOptions, queryString,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: OutboundConnectorControllerConnectorsEndpoint,
        sortable: true,
        defaultSortBy: OutboundConnectorControllerConnectorsEndpoint.sort.CREATED_AT,
        defaultSortDir: SortDir.ASC,
    })

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    // Fetch connectors — server applies q + typed filters (no client-side filtering).
    const {data: connectorsData, isLoading, error} = useOutboundConnectors(queryString)
    const connectors = useMemo(() => connectorsData?.content ?? [], [connectorsData])
    const totalItems = connectorsData?.totalElements || 0
    const totalPages = connectorsData?.totalPages || 0

    // System search for create dialog combobox
    const [systemSearchQuery, setSystemSearchQuery] = useState('')
    const {data: systemSearchData, isLoading: systemsLoading} = useQuery({
        queryKey: ['systems', 'search', systemSearchQuery],

        queryFn: () => services.systems.list({q: systemSearchQuery, size: 10}),
        enabled: createDialog.isOpen,
    })

    // Filter out systems that already have connectors
    const systemOptions: ComboboxOption[] = useMemo(() => {
        const systemIdsWithConnectors = new Set(connectors.map(c => c.targetSystem.id))
        return (systemSearchData?.content || [])
            .filter(s => !systemIdsWithConnectors.has(s.id))
            .map((s) => ({
                value: s.id,
                label: s.name,
                description: s.environment,
            }))
    }, [systemSearchData, connectors])

    // Create connector mutation
    const createMutation = useCreateConnector()

    const resetForm = () => {
        setFormData({targetSystemId: '', connectorType: OutboundConnectorType.SCIM_2_0})
        setSystemSearchQuery('')
    }

    const dismissDialog = () => {
        createDialog.close()
        resetForm()
        const captured = originSystemId
        setOriginSystemId(null)
        if (captured) {
            navigate(routes.systems.detail(captured))
        }
    }

    const handleCreate = () => {
        if (!formData.targetSystemId) return
        createMutation.mutate({
            targetSystemId: formData.targetSystemId,
            connectorType: formData.connectorType,
            enabled: false,
        }, {
            onSuccess: () => {
                dismissDialog()
            },
        })
    }

    const getOutboundConnectorTypeLabel = (type: string) => labelOf(connectorTypes, type)

    return (
        <>
            <PageLayout
                variant="list"
                icon={CONCEPT_ICON.connectorOutbound}
                title={i18n.connectors.allConnectors}
                count={totalItems}
                description={i18n.connectors.connectorCount({count: totalItems})}
                error={error}
                data-testid="outbound-connectors-list"
                actions={canCreate ? (
                    <Button onClick={createDialog.open} data-testid="create-connector-button">
                        <Icon icon={Plus} size="md" className="mr-2"/>
                        {i18n.connectors.createConnector}
                    </Button>
                ) : undefined}
                filters={
                    <UnifiedSearchBar
                        contract={OutboundConnectorControllerConnectorsEndpoint}
                        value={searchAndFilters}
                        onChange={setSearchAndFilters}
                        placeholder={i18n.connectors.filterPlaceholder}
                    />
                }
                pagination={totalItems > 0 ? (
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        totalItems={totalItems}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                        pageSizeOptions={pageSizeOptions}
                    />
                ) : undefined}
            >
                {isLoading ? (
                    <Stack gap="sm" data-testid="connectors-skeleton">
                        {Array.from({length: 5}).map((_, idx) => (
                            <Skeleton key={idx} className="h-12 w-full"/>
                        ))}
                    </Stack>
                ) : (
                    <Table data-testid="connectors-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead data-testid="col-system">
                                    <SortableHeader
                                        label={i18n.connectors.system}
                                        sortKey={OutboundConnectorListResponseFields.targetSystem}
                                        currentSortBy={sortBy}
                                        currentSortDir={sortDir}
                                        onSort={setSort}
                                    />
                                </TableHead>
                                <TableHead data-testid="col-type">
                                    <SortableHeader
                                        label={i18n.common.type}
                                        sortKey={OutboundConnectorListResponseFields.connectorType}
                                        currentSortBy={sortBy}
                                        currentSortDir={sortDir}
                                        onSort={setSort}
                                    />
                                </TableHead>
                                <TableHead data-testid="col-status">
                                    <SortableHeader
                                        label={i18n.common.status}
                                        sortKey={OutboundConnectorListResponseFields.enabled}
                                        currentSortBy={sortBy}
                                        currentSortDir={sortDir}
                                        onSort={setSort}
                                    />
                                </TableHead>
                                <TableHead data-testid="col-last-updated">
                                    <SortableHeader
                                        label={i18n.connectors.lastUpdated}
                                        sortKey={OutboundConnectorListResponseFields.updatedAt}
                                        currentSortBy={sortBy}
                                        currentSortDir={sortDir}
                                        onSort={setSort}
                                    />
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {connectors.map((connector) => (
                                <TableRow
                                    key={connector.id}
                                    data-testid={`connector-row-${connector.id}`}
                                    className="cursor-pointer"
                                    onClick={() => navigate(routes.connectors.outbound.detail(connector.id))}
                                >
                                    <TableCell className="font-medium"
                                             data-testid={`connector-name-${connector.id}`}>
                                        <EntityLink value={connector.targetSystem} stopPropagation/>
                                    </TableCell>
                                    <TableCell data-testid={`connector-type-${connector.id}`}>
                                        <Badge variant="outline">{getOutboundConnectorTypeLabel(connector.connectorType)}</Badge>
                                    </TableCell>
                                    <TableCell>
                                        {connector.enabled ? (
                                            <Badge variant={BadgeVariant.SUCCESS}>{i18n.common.enabled}</Badge>
                                        ) : (
                                            <Badge variant={BadgeVariant.WARNING}>{i18n.common.disabled}</Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {formatTimestamp(connector.updatedAt)}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {connectors.length === 0 && !isLoading && (
                                <TableEmptyState
                                    colSpan={4}
                                    hasFilters={hasActiveFilters}
                                    matchMessage={i18n.connectors.noConnectorsMatch}
                                    emptyMessage={i18n.connectors.noConnectorsFound}
                                />
                            )}
                        </TableBody>
                    </Table>
                )}
            </PageLayout>

            {/* Create Dialog */}
            <Dialog open={createDialog.isOpen} onOpenChange={createDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.connectors.createConnector}</DialogTitle>
                        <DialogDescription>
                            {i18n.connectors.createDescription}
                        </DialogDescription>
                    </DialogHeader>
                    <FormGrid className="py-4">
                        <FormGrid>
                            <Label htmlFor="targetSystem">{i18n.connectors.targetSystem}</Label>
                            <Combobox
                                options={systemOptions}
                                value={formData.targetSystemId || null}
                                onValueChange={(value) => setFormData({...formData, targetSystemId: value || ''})}
                                onSearch={setSystemSearchQuery}
                                placeholder={i18n.connectors.selectSystem}
                                emptyMessage={i18n.connectors.allSystemsHaveConnectors}
                                isLoading={systemsLoading}
                            />
                        </FormGrid>
                        <FormGrid>
                            <Label htmlFor={OutboundConnectorCreateRequestFields.connectorType}>{i18n.connectors.connectorType}</Label>
                            <Select value={formData.connectorType} onValueChange={(value) => setFormData({
                                ...formData,
                                connectorType: value
                            })}>
                                <SelectTrigger data-testid="connector-type-select">
                                    <SelectValue placeholder={i18n.connectors.selectOutboundConnectorType}/>
                                </SelectTrigger>
                                <SelectContent>
                                    {connectorTypes.map((type) => (
                                        <SelectItem key={type.code} value={type.code}>
                                            {type.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FormGrid>
                    </FormGrid>
                    <SaveCancelFooter
                        testIdPrefix="create"
                        onCancel={dismissDialog}
                        saving={createMutation.isPending}
                        disabled={!formData.targetSystemId}
                        saveLabel={i18n.common.create}
                        savingLabel={i18n.common.creating}
                        saveType="button"
                        onSave={handleCreate}
                    />
                </DialogContent>
            </Dialog>
        </>
    )
}
